/**
 * The last thing that touches a string before it is drawn.
 *
 * ## What this is not
 *
 * It is not the moderation. Moderation happens on the server, in
 * `packages/rule-engine/src/viewer-text.ts`, before the Action is ever sent —
 * because a hosted game is third-party code and anything decided inside the
 * sandbox is decided by the publisher, not by the platform. A game that wants
 * unmoderated text on screen does not need to defeat this file; it can simply
 * not call it.
 *
 * So this file deliberately contains no word list, no link policy, and no
 * judgement about content. Duplicating those here would give the platform two
 * answers to the same question and would invite the belief that turning this on
 * is what makes an overlay safe.
 *
 * ## What it is
 *
 * A guarantee about *shape*, applied at the moment of rendering: whatever string
 * arrives, what gets drawn is one line, of bounded length, made of characters
 * that are visible and that mean what they look like.
 *
 * That is worth having separately from moderation because the two fail
 * differently. Moderation answers "should this be shown"; a wrong answer shows a
 * bad comment. This answers "can this string do something other than be read";
 * a wrong answer lets a comment reorder the line it sits in, grow taller than
 * its row, or push the rest of the overlay off screen. The second class of
 * problem survives a correct moderation pass, because a string can be entirely
 * inoffensive and still be U+202E followed by four hundred combining acutes.
 *
 * It also covers the case where the SDK is not talking to TRAIDENT at all: a
 * game running against a local harness, or a developer feeding the overlay test
 * data by hand, gets the same shape guarantees as a live session.
 *
 * Self-contained on purpose. `@tryportal/game-sdk` has no runtime dependencies
 * because it is vendored into game bundles as loose ES modules
 * (`scripts/vendor-game-sdk.mjs`), and an import here would break every vendored
 * copy.
 */
/**
 * Characters that are invisible, that control direction, or that are not
 * characters at all.
 *
 * `\p{Cf}` is the important one: it covers the zero-width space, the bidi
 * overrides (U+202A-U+202E, U+2066-U+2069) that make displayed order differ from
 * logical order, and the Tags block (U+E0000-U+E007F) that can carry a whole
 * hidden ASCII string inside what looks like a single emoji.
 *
 * Written as escapes rather than literals so this line stays reviewable and
 * greppable — the same reasoning as the equivalent table in
 * `packages/feedback-sanitizer/src/normalize.ts`, which this mirrors.
 */
const INVISIBLE = new RegExp([
    '\\p{Cc}', // C0/C1 controls
    '\\p{Cf}', // format: zero-width, bidi overrides, soft hyphen, Tags block
    '\\p{Cs}', // lone surrogates
    '\\p{Co}', // private use: renders as tofu or as a font's own glyph
    '\\p{Cn}', // unassigned
    '[\\uFE00-\\uFE0F]', // variation selectors (category Mn, so not caught above)
    '[\\u{E0100}-\\u{E01EF}]', // variation selectors supplement
    '[\\u115F\\u1160\\u3164\\uFFA0]', // Hangul fillers: category Lo, render as nothing
    '[\\u{1D173}-\\u{1D17A}]', // musical formatting
].join('|'), 'gu');
/**
 * The invisible characters ordinary people actually send.
 *
 * A family emoji is several codepoints held together by zero-width joiners, and
 * U+FE0F is what makes an emoji render as an emoji rather than as a dingbat.
 * Removing them would turn 👨‍👩‍👧 into three separate people and ❤️ into a
 * monochrome heart, which is a visible bug in ordinary chat rather than a
 * defence against anything.
 */
const KEEP = new Set([
    '\u200D', // zero-width joiner: holds multi-codepoint emoji together
    '\uFE0E', // text presentation selector
    '\uFE0F', // emoji presentation selector
]);
const COMBINING = /[\p{Mn}\p{Me}]/u;
const DEFAULT_MAX_COMBINING = 2;
/**
 * Grapheme segmentation, when the runtime has it.
 *
 * `Intl.Segmenter` is in every browser that can run a modern game bundle, but
 * the SDK also runs in test harnesses and older embedded webviews, so its
 * absence must degrade rather than throw. The fallback counts code points via
 * the string iterator, which never splits a surrogate pair — it can split a ZWJ
 * emoji sequence, which is a cosmetic loss, not a correctness one.
 */
const segmenter = typeof Intl !== 'undefined' && typeof Intl.Segmenter === 'function'
    ? new Intl.Segmenter(undefined, { granularity: 'grapheme' })
    : null;
function truncate(value, limit) {
    if (limit <= 0)
        return '';
    if (segmenter !== null) {
        let count = 0;
        let end = 0;
        for (const piece of segmenter.segment(value)) {
            if (count >= limit)
                break;
            count += 1;
            end = piece.index + piece.segment.length;
        }
        return value.slice(0, end);
    }
    let count = 0;
    let out = '';
    for (const char of value) {
        if (count >= limit)
            break;
        count += 1;
        out += char;
    }
    return out;
}
/**
 * Make one string safe to draw on a single row.
 *
 * Returns the empty string when nothing legible survives, which callers treat as
 * "do not render a row at all" rather than as "render an empty row": an empty
 * bubble attributed to a viewer is still something the audience sees.
 */
export function guardText(value, options) {
    if (typeof value !== 'string' || value.length === 0)
        return '';
    const visible = value.replace(INVISIBLE, (matched) => (KEEP.has(matched) ? matched : ''));
    // Every whitespace run becomes one space. A newline in a value that is about
    // to be assigned to `textContent` inside a single-row flex item either does
    // nothing (in which case it was noise) or makes that row taller than its
    // neighbours (in which case it was a layout attack). Collapsing removes the
    // difference between those two cases.
    const single = visible.replace(/\s+/gu, ' ').trim();
    if (single.length === 0)
        return '';
    const markLimit = options.maxCombiningMarks ?? DEFAULT_MAX_COMBINING;
    let capped = '';
    let marks = 0;
    let base = false;
    for (const char of single) {
        if (COMBINING.test(char)) {
            // A combining mark with nothing before it attaches to whatever the
            // renderer drew last — the label, or the row above. Dropping leading
            // marks is the other half of capping runs.
            if (!base || marks >= markLimit)
                continue;
            marks += 1;
            capped += char;
            continue;
        }
        marks = 0;
        base = true;
        capped += char;
    }
    return truncate(capped, options.maxLength).trim();
}
